import {
  AuthApiPath,
  CONTAINS_HTML_REGEX,
  CONTAINS_LETTER_REGEX,
  CONTAINS_NUMBER_REGEX,
  CONTAINS_URL_REGEX,
  CONTENT_TYPE_JSON,
  INVALID_NAME_REGEX,
  VALID_EMAIL_REGEX,
  VALID_USERNAME_REGEX,
} from "@/lib/auth/constants";
import newrelic from "@/lib/monitoring/newrelic";
import { buildApiEndpoint } from "@/lib/auth/buildApiEndpoint";

export type RegistrationValidationParams = Partial<{
  name: string;
  username: string;
  email: string;
  confirm_email: string;
  password: string;
  country: string;
  // Optionally, for password validation context:
  reset_password_page: string | boolean;
}>;

type ValidationDecisions = {
  // null, undefined of "" if valid, or error message if invalid
  name?: string;
  username?: string;
  email?: string;
  confirm_email?: string;
  password?: string;
  country?: string;
};
export type RegistrationValidationResult = {
  validation_decisions?: ValidationDecisions;

  // Present if username is invalid or taken
  username_suggestions?: string[];

  // Not part of the response
  rate_limited?: boolean;
};

type ValidatorReturn = string | undefined;

export function validatePassword(password: string): ValidatorReturn {
  // Unlike the other validators this is stolen from LMS, and uses the hard coded baseline, we still need to ask LMS
  // if it's good.
  if (!password) {
    return "Password is required.";
  }
  if (password.length < 8) {
    return "Password must be at least 8 characters.";
  }
  if (password.length > 75) {
    return "Password must be at most 75 characters.";
  }
  // This part is stolen from the rules on the edX AuthN UI
  if (!CONTAINS_LETTER_REGEX.test(password)) {
    return "Password must contain at least one letter.";
  }
  if (!CONTAINS_NUMBER_REGEX.test(password)) {
    return "Password must contain at least one number.";
  }
  return undefined;
}

function validateName(name: string): ValidatorReturn {
  if (!name.trim()) {
    return "Name is required.";
  }
  if (
    CONTAINS_URL_REGEX.test(name) ||
    CONTAINS_HTML_REGEX.test(name) ||
    INVALID_NAME_REGEX.test(name)
  ) {
    return "Invalid name.";
  }
  return undefined;
}

function validateUsername(username: string): ValidatorReturn {
  if (!username || username.length <= 1 || username.length > 30) {
    return "Username must be 2-30 characters.";
  }
  const usernameRegex = new RegExp(VALID_USERNAME_REGEX, "i");
  if (!VALID_USERNAME_REGEX.test(username)) {
    return "Invalid username.";
  }
  return undefined;
}

export function validateEmail(email: string): ValidatorReturn {
  if (!email) {
    return "Email is required.";
  }

  if (email.length <= 2) {
    return "Invalid email address.";
  }

  const emailRegex = new RegExp(VALID_EMAIL_REGEX, "i");
  if (!emailRegex.test(email)) {
    return "Invalid email address.";
  }

  return undefined;
}

function localValidation(
  params: RegistrationValidationParams,
): RegistrationValidationResult | undefined {
  let hasError = false;
  let result = {} as ValidationDecisions;

  if (params.name) {
    const msg = validateName(params.name);
    if (msg) {
      hasError = true;
      result.name = msg;
    }
  }
  if (params.username) {
    const msg = validateUsername(params.username);
    if (msg) {
      hasError = true;
      result.username = msg;
    }
  }
  if (params.email) {
    const msg = validateEmail(params.email);
    if (msg) {
      hasError = true;
      result.email = msg;
    }
  }
  if (params.confirm_email) {
    const msg = validateEmail(params.confirm_email);
    if (msg) {
      hasError = true;
      result.confirm_email = msg;
    }
  }
  if (params.password) {
    const msg = validatePassword(params.password);
    if (msg) {
      hasError = true;
      result.password = msg;
    }
  }

  if (hasError) {
    return { validation_decisions: result } as RegistrationValidationResult;
  } else {
    return;
  }
}

export default async function validateRegistrationFields(
  params: RegistrationValidationParams,
): Promise<RegistrationValidationResult> {
  try {
    // LMS Can rate limit us so let's do some local validation attempts first.
    const localValidationResult = await localValidation(params);

    if (localValidationResult) {
      return localValidationResult;
    }

    const headers = {
      accept: "application/json, text/plain, */*",
      "cache-control": "no-cache",
      pragma: "no-cache",
      "Content-Type": CONTENT_TYPE_JSON,
    } as Record<string, string>;

    const res = await fetch(buildApiEndpoint(AuthApiPath.RegFieldValidation), {
      headers: {
        ...headers,
      },
      body: JSON.stringify(params),
      method: "POST",
    });

    if (!res.ok && res.status !== 403 /* Rate Limited */) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error(
        `validateRegistrationFields Status code: ${res.status}, ${JSON.stringify(await res.json())}`,
      );
    } else if (res.status === 403) {
      return { rate_limited: true } as RegistrationValidationResult;
    }

    return (await res.json()) as RegistrationValidationResult;
  } catch (e) {
    newrelic.noticeError(e as Error);
    throw e;
  }
}
